import type { NpcHullTier } from "./types";

// Drone loadout — SUPERSEDES 16.23's per-tier capacity table (16.87, Don's
// direction). One Drone Module per ship, still size-locked so an S/M/L/C hull
// only takes S/M/L/C drones (enforced in loadableDroneCount).
//
// 16.23's table read 1/3/10/15 by tier, i.e. a HIGHER tier flies MORE drones.
// 16.74 then gave drones real volume (4/8/15/25 m3) on the opposite premise --
// that a bigger drone is a bigger object. Both were locked, and together they
// grew the bay 4 m3 -> 375 m3 (93x) and the bay's DPS 168x against every
// weapon line's 5.83x, which is what let a T4 hauler out-damage a T4 gunship
// (16.86's Finding 3). Don's ruling resolves it in favour of the volume
// reading: **the bay grows to hold larger drones, not more of them.**
//
// So the deployed complement is constant across tiers -- the drones themselves
// already scale (per-drone EHP and DPS both grow ~25x, 16.23) and scaling the
// count on top of that was the double-count. It varies by ROLE instead:
export const DRONE_COMPLEMENT: Record<string, number> = {
  // Drones are 100% of a mining barge's combat capability -- every Digger
  // variant at every tier has 0 weapon DPS. It gets the largest complement.
  Digger: 3,
  // A hauler has real guns (15/27/49/88 by tier); drones supplement them.
  Packmule: 2
};

// Spare drones carried beyond the deployed complement, so a captain can absorb
// losses across a few missions without a Market trip (Don's direction).
// Derived from the real loss rate rather than picked: enemy fire goes to a
// deployed drone 10% of the time (DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT below) and
// a deployed drone never self-repairs, so simulating the roster's winnable
// engagements gives 0.08-0.16 drones lost per fight at T1-T3 and 0.44 at T4
// (worst single fight 0.84). Over a three-mission run that is 0.25-0.47 at
// T1-T3 and 1.32 at T4 -- so two spares covers "a few missions" at every tier,
// and still covers two consecutive worst-case T4 fights (0.84 x 2 = 1.68 < 2).
export const DRONE_SPARES = 2;

/** How many drones fly at once. 0 for a role with no default bay. */
export function droneComplement(role: string): number {
  return DRONE_COMPLEMENT[role] ?? 0;
}

/** How many drones the bay holds in total: the complement plus its spares. */
export function droneBayCapacity(role: string): number {
  const complement = droneComplement(role);
  return complement === 0 ? 0 : complement + DRONE_SPARES;
}

// Per-drone combat stats by tier (16.23): ~2x a same-tier Micro Pirate's
// EHP, ~1x its DPS.
export const DRONE_STATS: Record<NpcHullTier, { ehp: number; dps: number }> = {
  T1: { ehp: 644, dps: 2.5 },
  T2: { ehp: 1160, dps: 5.8 },
  T3: { ehp: 2084, dps: 13.5 },
  T4: { ehp: 3762, dps: 63.1 }
};

// Docked drones self-repair at a flat rate of their own max EHP over this
// many seconds. **Amended in 16.74:** this used to apply ONLY during a
// Wave encounter's inter-wave recovery gap; a docked drone now repairs
// steadily whenever it's in the bay (Don's direction), which is what
// makes recalling a damaged drone a real tactical choice rather than just
// parking it. Deployed drones never self-repair, in or out of a gap.
export const DRONE_HEAL_SECONDS = 45;

// Drones as a real purchasable good (16.74). A destroyed drone is gone
// for good (16.23) and the bay stays empty until the captain buys a
// replacement at any Market -- so drones need a real price and a real
// volume, and they live in `state.cargo` like every other good rather
// than in a separate inventory the rest of the game would have to learn
// about. The Expedition View loads the bay from cargo at mount, capped by
// DRONE_CAPACITY for the hull being flown.
export interface DroneCatalogEntry {
  name: string;
  hullTier: NpcHullTier;
  priceCr: number;
  volumeM3: number;
}

// **Price derivation** (rather than picked numbers), unchanged in rule and
// REPRICED in 16.87 because its input changed: a drone is priced so that
// filling an EMPTY bay costs roughly **40% of a same-tier hull's own Shipyard
// price** -- expensive enough to hurt, cheap enough to actually replace.
// Referenced against the Digger line (the largest bay, and the role for which
// drones are the entire weapon system) at its 5-drone capacity:
//   T1 0.40 x   508 / 5 =  41 CR      T3 0.40 x 1,453 / 5 = 116 CR
//   T2 0.40 x   858 / 5 =  69 CR      T4 0.40 x 2,610 / 5 = 209 CR
//
// 16.74 had to apologise for this table: under the old 1/3/10/15 capacity the
// same 40% rule drove per-drone price DOWN as tiers rose (180/110/65/60 CR),
// and it called that "the honest consequence of the locked capacity curve --
// a Capital bay is a rack of many small expendable units, not one big one."
// That reading is exactly what 16.87 overturned. With a constant complement
// the price now RISES with tier, which is what the volume column said all
// along: a Capital Drone is 6.25x the size of a Small one, so it should cost
// more, not a third as much.
//
// Full-bay cost lands at 40% of the reference hull by construction, and ~38%
// of a Packmule's cheaper hull -- inside 16.74's intended band for both.
//
// 16.92 multiplied these by 100 along with the hull reprice. They are anchored
// to hull cost, not chosen independently, so leaving them alone would have
// dropped a full drone bay from 40% of a hull to 0.4% of one -- free. The test
// that guards the 40% derivation is what caught it, which is the whole reason
// that test asserts a RATIO rather than the four prices.
export const DRONE_CATALOG: DroneCatalogEntry[] = [
  { name: "Small Drone", hullTier: "T1", priceCr: 4100, volumeM3: 4 },
  { name: "Medium Drone", hullTier: "T2", priceCr: 6900, volumeM3: 8 },
  { name: "Large Drone", hullTier: "T3", priceCr: 11600, volumeM3: 15 },
  { name: "Capital Drone", hullTier: "T4", priceCr: 20900, volumeM3: 25 }
];

/**
 * The bay's real volume: what a full loadout of this hull's own drone type
 * actually occupies (16.87). Drones live in `state.cargo` (16.74), so this is
 * a genuine cargo commitment rather than a separate free hold -- 29% of a
 * Digger I's general cargo down to 8% of a Digger IV's, since the bay grows
 * 6.25x across tiers while cargo grows 22x.
 */
export function droneBayVolumeM3(role: string, hullTier: NpcHullTier): number {
  return droneBayCapacity(role) * droneEntryForHull(hullTier).volumeM3;
}

export function droneEntryForHull(hullTier: NpcHullTier): DroneCatalogEntry {
  return DRONE_CATALOG.find((d) => d.hullTier === hullTier)!;
}

export function isDroneItem(name: string): boolean {
  return DRONE_CATALOG.some((d) => d.name === name);
}

export function droneVolumeM3(name: string): number | undefined {
  return DRONE_CATALOG.find((d) => d.name === name)?.volumeM3;
}

// How many drones are actually available to load into the bay: what the
// captain is carrying of this hull's matching drone type, capped by the
// hull's own bay capacity. A drone of the wrong tier is just cargo -- it
// doesn't fit this bay. This is the size-lock Don asked to be re-checked:
// an S/M/L/C hull only ever loads S/M/L/C drones, and it is enforced by the
// drone's *name* matching the hull's tier, so a hold full of Small Drones is
// simply inert aboard a Capital hull.
export function loadableDroneCount(
  role: string,
  hullTier: NpcHullTier,
  cargo: Record<string, number>
): number {
  const entry = droneEntryForHull(hullTier);
  const carried = Math.floor(cargo[entry.name] ?? 0);
  return Math.max(0, Math.min(droneBayCapacity(role), carried));
}

// v1 default: enemy AI weights target selection 90% ship / 10% split across
// deployed drones (16.23) -- an explicit starting default for playtesting,
// not a final balance number (CLAUDE.md).
export const DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT = 0.9;

// Bay-total damping (16.23): a pre-combat "how strong is a full bay" summary
// stat -- NOT a live per-tick mechanic. The tick loop (combatResolutionLoop)
// rolls each deployed drone's attack individually, matching 16.30's explicit
// "every active weapon, drone, and enemy unit independently rolls once per
// tick." Bay-EHP damping is noted in 16.23 itself as "mostly vestigial"
// since the v1 targeting correction tracks each drone's own HP individually.
export function bayTotalDps(perDroneDps: number, droneCount: number): number {
  return perDroneDps * Math.pow(droneCount, 0.7);
}

export function bayTotalEhp(perDroneEhp: number, droneCount: number): number {
  return perDroneEhp * Math.pow(droneCount, 0.85);
}

// Only Miner (Digger) and Hauler (Packmule) fit a Drone Module in their
// current fixed default loadout (16.23: "No other role... currently fits a
// Drone Module in its default loadout... the mechanic is available to them
// but not exercised in any current fixed build"). ships.json doesn't carry
// per-module fitting data, so this infers "has a drone bay" from the
// ship's own role name -- the only signal the fixed roster actually gives.
const DEFAULT_DRONE_BAY_ROLES = new Set(["Digger", "Packmule"]);

export function shipHasDefaultDroneBay(role: string): boolean {
  return DEFAULT_DRONE_BAY_ROLES.has(role);
}

// "Player DPS" for the Mission Difficulty Score engine (16.32, clarified)
// is weapon DPS + Drone Bay DPS combined, whenever a bay is fitted -- a
// Miner/Hauler's 0 weapon DPS is not "0 DPS" for this algorithm. Uses the
// bay's damped total (16.23), not a naive per-drone sum.
//
// Counts the DEPLOYED COMPLEMENT, not the bay's full capacity (16.87): spares
// sit in the bay and contribute nothing until a loss pulls one out to replace
// a casualty. Counting them was part of why the old estimate ran so hot.
export function effectivePlayerDps(weaponDps: number, hullTier: NpcHullTier, role: string): number {
  const complement = droneComplement(role);
  if (complement === 0) {
    return weaponDps;
  }
  return weaponDps + bayTotalDps(DRONE_STATS[hullTier].dps, complement);
}
